import { Link, Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { listManagedWorkspaces } from "../../api/workspaces";
import { listManagedWorkspaceBookings } from "../../api/workspaceBookings";
import { listManagedWorkspaceLeases } from "../../api/workspaceLeases";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { useRentalsFeature } from "../../context/RentalsFeatureContext";
import { buildHostUrl } from "../../config/hostRouting";
import {
  formatWorkspaceMoney,
  getWorkspaceDisplayName,
  titleCaseLabel,
} from "../../utils/workspacePresentation";
import { getWorkspaceFinanceSnapshot } from "../../utils/workspaceBillingPresentation";
import { canAccessMarketplaceOperatorShell } from "../../utils/marketplaceOperatorAccess";
import {
  getMarketplaceOperatorAgreementsPath,
  getMarketplaceOperatorPaymentsPath,
  getMarketplaceOperatorSettingsPath,
  getMarketplaceOperatorWorkspaceCreatePath,
  getMarketplaceOperatorWorkspacesPath,
} from "../../utils/marketplaceOperatorRoutes";
import MarketplaceShell from "./MarketplaceShell";

function MetricCard({ label, value, caption, to, actionLabel }) {
  const body = (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </p>
      <p className="mt-3 text-3xl font-semibold text-slate-950">{value}</p>
      <p className="mt-2 text-sm leading-6 text-slate-600">{caption}</p>
      {actionLabel ? (
        <span className="mt-4 inline-flex text-sm font-semibold text-[#2f3566]">
          {actionLabel}
        </span>
      ) : null}
    </>
  );

  if (!to) {
    return (
      <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
        {body}
      </article>
    );
  }

  return (
    <Link
      to={to}
      className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      {body}
    </Link>
  );
}

function PrimaryAction({ to, children, variant = "primary" }) {
  const className =
    variant === "secondary"
      ? "inline-flex rounded-full border border-[#2f3566]/20 bg-white px-4 py-2 text-sm font-semibold text-[#2f3566] hover:bg-[#2f3566]/5"
      : "inline-flex rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-600";
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function getWorkspaceRentAmount(workspace = {}) {
  const setup = workspace.booking_rules_json?.workspace_listing_setup || {};
  return Number(
    setup.rentTerms?.baseRent ||
      workspace.base_rent_amount ||
      workspace.monthly_rate ||
      workspace.weekly_rate ||
      workspace.daily_rate ||
      workspace.hourly_rate ||
      workspace.price ||
      0
  );
}

function getWorkspaceIssues(workspace = {}) {
  const setup = workspace.booking_rules_json?.workspace_listing_setup || {};
  const issues = [];
  if (!getWorkspaceDisplayName(workspace) || getWorkspaceDisplayName(workspace) === "Workspace") {
    issues.push("Workspace name is missing.");
  }
  if (workspace.marketplace_status === "published" && workspace.visibility !== "public") {
    issues.push("Published workspace is not public.");
  }
  if (workspace.marketplace_status === "published" && getWorkspaceRentAmount(workspace) <= 0) {
    issues.push("Published workspace needs rent terms.");
  }
  if (workspace.marketplace_status === "published" && !setup.independentTerms) {
    issues.push("Independent professional terms need review.");
  }
  if (workspace.marketplace_status === "published" && workspace.is_available === false) {
    issues.push("Published workspace is marked unavailable.");
  }
  return issues;
}

function getActivityDate(record = {}) {
  const value =
    record.updated_at ||
    record.created_at ||
    record.start_time ||
    record.start_date ||
    record.created_at_utc;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function formatActivityDate(date) {
  if (!date) return "Recently";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isPublishedWorkspace(workspace = {}) {
  return workspace.marketplace_status === "published" && workspace.visibility === "public";
}

function isDraftWorkspace(workspace = {}) {
  const status = String(workspace.marketplace_status || workspace.rental_status || "").toLowerCase();
  return !isPublishedWorkspace(workspace) && ["", "draft", "private", "hidden"].includes(status);
}

export default function MarketplaceOperatorHubPage() {
  const { token, user } = useAuth();
  const runtimeBrand = useBrand();
  const {
    enabled,
    loading: rentalsLoading,
    error: rentalsError,
    settingsReady,
    operationalReady,
    discoverability,
    marketplaceEnabled,
    defaultRequiresApproval,
  } = useRentalsFeature();
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [requests, setRequests] = useState([]);
  const [agreements, setAgreements] = useState([]);

  const roleKey = String(user?.role || "").toLowerCase();
  const isOperator = canAccessMarketplaceOperatorShell({ role: roleKey, user });
  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const salonName =
    user?.salon_name ??
    (typeof window !== "undefined" ? window.localStorage.getItem("salon_name") : "");

  useEffect(() => {
    if (!token || !isOperator || !enabled || !salonId) {
      setInventoryLoading(false);
      setInventoryError("");
      setWorkspaces([]);
      setRequests([]);
      setAgreements([]);
      return undefined;
    }

    let active = true;
    setInventoryLoading(true);
    setInventoryError("");
    const inventoryRequests = [
      {
        key: "workspaces",
        label: "Unable to load workspaces.",
        load: () => listManagedWorkspaces(token, salonId),
        apply: setWorkspaces,
      },
      {
        key: "rental requests",
        label: "Unable to load rental requests.",
        load: () => listManagedWorkspaceBookings(token, salonId),
        apply: setRequests,
      },
      {
        key: "rental agreements",
        label: "Unable to load rental agreements.",
        load: () => listManagedWorkspaceLeases(token, salonId),
        apply: setAgreements,
      },
    ];

    Promise.allSettled(inventoryRequests.map((request) => request.load()))
      .then((results) => {
        if (!active) return;
        const errors = [];
        results.forEach((result, index) => {
          const request = inventoryRequests[index];
          if (result.status === "fulfilled") {
            request.apply(Array.isArray(result.value) ? result.value : []);
            return;
          }
          request.apply([]);
          errors.push(request.label);
        });
        setInventoryError(
          errors.length
            ? `${errors.join(" ")} Other dashboard metrics may still be shown.`
            : ""
        );
      })
      .catch((err) => {
        if (!active) return;
        setInventoryError(err.message || "Unable to load your dashboard.");
      })
      .finally(() => {
        if (active) setInventoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [enabled, isOperator, salonId, token]);

  const dashboard = useMemo(() => {
    const publishedWorkspaces = workspaces.filter(isPublishedWorkspace);
    const draftWorkspaces = workspaces.filter(isDraftWorkspace);
    const pendingRequests = requests.filter((request) =>
      ["pending", "requested", "pending_approval"].includes(
        String(request.status || "").toLowerCase()
      )
    );
    const approvedRequests = requests.filter((request) =>
      ["approved", "confirmed", "active"].includes(String(request.status || "").toLowerCase())
    );
    const activeAgreements = [
      ...approvedRequests,
      ...agreements.filter((agreement) =>
        ["approved", "active", "signed", "renewal_due"].includes(
          String(agreement.status || "").toLowerCase()
        )
      ),
    ];
    const paymentsDue = requests.reduce(
      (sum, request) => sum + Number(getWorkspaceFinanceSnapshot(request).amountDueNow || 0),
      0
    );
    const workspaceIssues = workspaces.flatMap((workspace) =>
      getWorkspaceIssues(workspace).map((issue) => ({
        workspaceId: workspace.id,
        workspaceName: getWorkspaceDisplayName(workspace),
        issue,
      }))
    );
    const setupIssues = [
      ...(!settingsReady ? [{ issue: "Rental settings need review." }] : []),
      ...(!operationalReady ? [{ issue: "Operational readiness is incomplete." }] : []),
      ...(!marketplaceEnabled ? [{ issue: "Marketplace publishing is private or disabled." }] : []),
      ...workspaceIssues,
    ];
    const recentActivity = [
      ...requests.map((request) => {
        const active = ["approved", "confirmed", "active"].includes(
          String(request.status || "").toLowerCase()
        );
        return {
          id: `request-${request.id}`,
          date: getActivityDate(request),
          title: active ? "Rental agreement approved" : "Rental request received",
          detail:
            request.renter_name ||
            request.renter_email ||
            getWorkspaceFinanceSnapshot(request).workspaceName ||
            "Independent professional",
        };
      }),
      ...agreements.map((agreement) => ({
        id: `agreement-${agreement.id}`,
        date: getActivityDate(agreement),
        title: "Rental agreement updated",
        detail: titleCaseLabel(agreement.status, "Agreement status updated"),
      })),
      ...workspaces.map((workspace) => ({
        id: `workspace-${workspace.id}`,
        date: getActivityDate(workspace),
        title: isPublishedWorkspace(workspace) ? "Workspace published" : "Workspace draft updated",
        detail: getWorkspaceDisplayName(workspace),
      })),
    ]
      .sort((a, b) => {
        const left = a.date ? a.date.getTime() : 0;
        const right = b.date ? b.date.getTime() : 0;
        return right - left;
      })
      .slice(0, 6);

    return {
      publishedWorkspaces,
      draftWorkspaces,
      pendingRequests,
      activeAgreements,
      paymentsDue,
      setupIssues,
      recentActivity,
    };
  }, [agreements, marketplaceEnabled, operationalReady, requests, settingsReady, workspaces]);

  const ellyannaSetupUrl = useMemo(
    () => buildHostUrl(runtimeBrand.opsHost, "/setup"),
    [runtimeBrand.opsHost]
  );

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!isOperator) {
    return <Navigate to="/account/profile" replace />;
  }

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[28px] border border-white/70 bg-white/75 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur">
          <div className="grid gap-6 border-b border-slate-200/70 px-6 py-7 text-slate-900 lg:grid-cols-[1.05fr,0.95fr]">
            <div>
              <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-rose-600">
                MyBoothShare Dashboard
              </p>
              <h1 className="mt-4 text-3xl font-semibold leading-tight text-slate-950">
                Workspace Rental Command Center
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
                Track live workspaces, pending rental requests, active rental agreements,
                payments due, and setup issues from one operator view.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-sm">
                <span className="rounded-full border border-[#2f3566]/15 bg-white px-4 py-2 font-semibold text-[#2f3566]">
                  {salonName || "Current salon"}
                </span>
                <span className="rounded-full border border-[#2f3566]/15 bg-white px-4 py-2 font-semibold text-[#2f3566]">
                  {titleCaseLabel(roleKey, "Operator")}
                </span>
              </div>
            </div>
            <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                Readiness
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Rentals
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-950">
                    {rentalsLoading ? "Checking..." : enabled ? "Enabled" : "Not enabled"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Publishing
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-950">
                    {marketplaceEnabled ? "Ready" : "Private"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Discoverability
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-700">
                    {titleCaseLabel(discoverability)}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Requests
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-700">
                    {defaultRequiresApproval ? "Approval required" : "Can approve automatically"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 px-6 py-6">
            {rentalsError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {rentalsError}
              </div>
            ) : null}

            {!enabled ? (
              <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                  Rentals not enabled yet
                </p>
                <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                  Turn rentals on in Ellyanna first
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-700">
                  This salon can use MyBoothShare after the rentals feature is enabled.
                  Once enabled, return here to manage workspaces, rental agreements,
                  payments, and publication readiness.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={ellyannaSetupUrl}
                    className="inline-flex rounded-full bg-[#2f3566] px-4 py-2 text-sm font-semibold text-white hover:bg-[#252a53]"
                  >
                    Open Ellyanna Setup
                  </a>
                  <Link
                    to="/search"
                    className="inline-flex rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                  >
                    Browse marketplace view
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  <PrimaryAction to={getMarketplaceOperatorWorkspaceCreatePath()}>
                    List a Workspace
                  </PrimaryAction>
                  <PrimaryAction
                    to={getMarketplaceOperatorAgreementsPath("?status=pending")}
                    variant="secondary"
                  >
                    Review Rental Requests
                  </PrimaryAction>
                  <PrimaryAction
                    to={getMarketplaceOperatorAgreementsPath("?status=active")}
                    variant="secondary"
                  >
                    View Active Agreements
                  </PrimaryAction>
                  <PrimaryAction to={getMarketplaceOperatorPaymentsPath()} variant="secondary">
                    Open Payments
                  </PrimaryAction>
                  <PrimaryAction
                    to={`${getMarketplaceOperatorWorkspacesPath()}?tab=compliance`}
                    variant="secondary"
                  >
                    Fix Setup Issues
                  </PrimaryAction>
                </div>

                <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard
                    label="Published Workspaces"
                    value={inventoryLoading ? "..." : dashboard.publishedWorkspaces.length}
                    caption="Listings visible to independent professionals."
                    to={getMarketplaceOperatorWorkspacesPath()}
                    actionLabel="Open workspaces"
                  />
                  <MetricCard
                    label="Draft Workspaces"
                    value={inventoryLoading ? "..." : dashboard.draftWorkspaces.length}
                    caption="Listings still being prepared or kept private."
                    to={getMarketplaceOperatorWorkspacesPath()}
                    actionLabel="Review drafts"
                  />
                  <MetricCard
                    label="Pending Rental Requests"
                    value={inventoryLoading ? "..." : dashboard.pendingRequests.length}
                    caption="Requests waiting for approval or decline."
                    to={getMarketplaceOperatorAgreementsPath("?status=pending")}
                    actionLabel="Review requests"
                  />
                  <MetricCard
                    label="Active Rental Agreements"
                    value={inventoryLoading ? "..." : dashboard.activeAgreements.length}
                    caption="Approved workspace rental relationships."
                    to={getMarketplaceOperatorAgreementsPath("?status=active")}
                    actionLabel="View agreements"
                  />
                </section>

                <section className="grid gap-4 lg:grid-cols-[0.85fr,1.15fr]">
                  <MetricCard
                    label="Payments Due"
                    value={inventoryLoading ? "..." : formatWorkspaceMoney(dashboard.paymentsDue)}
                    caption="Deposits or payments currently due from rental requests and agreements."
                    to={getMarketplaceOperatorPaymentsPath()}
                    actionLabel="Open payments"
                  />
                  <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Warnings / Setup Issues
                        </p>
                        <h2 className="mt-3 text-2xl font-semibold text-slate-950">
                          {inventoryLoading ? "..." : dashboard.setupIssues.length}
                        </h2>
                      </div>
                      <Link
                        to={`${getMarketplaceOperatorWorkspacesPath()}?tab=compliance`}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        Fix Setup Issues
                      </Link>
                    </div>
                    <div className="mt-4 grid gap-3">
                      {dashboard.setupIssues.slice(0, 4).map((item, index) => (
                        <div
                          key={`${item.workspaceId || "settings"}-${index}`}
                          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                        >
                          <span className="font-semibold text-amber-950">
                            {item.workspaceName || "Account setup"}:
                          </span>{" "}
                          {item.issue}
                        </div>
                      ))}
                      {!inventoryLoading && !dashboard.setupIssues.length ? (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                          No setup issues are blocking the current rental workflow.
                        </div>
                      ) : null}
                    </div>
                  </article>
                </section>

                {inventoryError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {inventoryError}
                  </div>
                ) : null}

                <section className="grid gap-4 lg:grid-cols-[1.05fr,0.95fr]">
                  <article className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Recent Activity
                        </p>
                        <h2 className="mt-3 text-xl font-semibold text-slate-950">
                          Workspace and rental updates
                        </h2>
                      </div>
                      <Link
                        to={getMarketplaceOperatorAgreementsPath()}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        Open Rental Agreements
                      </Link>
                    </div>
                    <div className="mt-5 grid gap-3">
                      {dashboard.recentActivity.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="font-semibold text-slate-950">{item.title}</span>
                            <span className="text-slate-500">{formatActivityDate(item.date)}</span>
                          </div>
                          <div className="mt-1 text-slate-600">{item.detail}</div>
                        </div>
                      ))}
                      {!inventoryLoading && !dashboard.recentActivity.length ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                          Activity will appear after workspaces, rental requests, or rental agreements are created.
                        </div>
                      ) : null}
                    </div>
                  </article>

                  <article className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Dashboard routing
                    </p>
                    <h2 className="mt-3 text-xl font-semibold text-slate-950">
                      Use the simplified MyBoothShare model
                    </h2>
                    <dl className="mt-5 grid gap-3 text-sm text-slate-600">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                        <dt className="font-semibold text-slate-950">Workspace setup</dt>
                        <dd className="mt-1">Managed under Workspaces.</dd>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                        <dt className="font-semibold text-slate-950">Rental requests</dt>
                        <dd className="mt-1">Reviewed under Rental Agreements.</dd>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                        <dt className="font-semibold text-slate-950">Money movement</dt>
                        <dd className="mt-1">Handled from Payments.</dd>
                      </div>
                    </dl>
                    <div className="mt-5">
                      <Link
                        to={getMarketplaceOperatorSettingsPath()}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        Open Settings
                      </Link>
                    </div>
                  </article>
                </section>
              </>
            )}
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
